import { colorToRGBA } from "src/utils/colorToRgba";
import React, { SyntheticEvent } from "react";
import { Flex } from "src/elements/Box/Box";
import { Color } from "src/elements/Color/Color";
import { HorizontalDivider } from "src/elements/Dividers";
import { Text } from "src/elements/Text/Text";
import styled from "styled-components";

import { ButtonDropdownOption } from "../store/types";
import { Button } from "../Button";
import { Icon } from "src/elements/Icon/Icon";
import { Link } from "src/elements/Link";
import { InfoTooltip } from "src/elements/InfoTooltip/InfoTooltip";

const DropdownElement = styled(Flex)`
  margin: 5px 0;
  flex-direction: column;
  background-color: ${Color.white};
  border-radius: 3px;
  cursor: default;
  max-height: 320px;
  overflow-y: auto;
`;

const DropdownOption = styled(Flex)<{
  hoverColor: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isWithButtons: boolean;
}>`
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  min-height: 35px;
  padding: 0 10px;
  user-select: none;
  padding-right: ${({ isWithButtons }) => isWithButtons && "5px"};

  &:hover {
    background-color: ${({ hoverColor }) => colorToRGBA(hoverColor, 0.2)};
  }

  ${({ isDisabled, isLoading }) =>
    (isDisabled || isLoading) &&
    `
  pointer-events: none;
  opacity: 0.3;`}
`;

interface Props {
  dropdown: ButtonDropdownOption[];
  textColor: string;
  borderColor: string;
}

export const Dropdown = ({ dropdown, textColor, borderColor }: Props) => (
  <DropdownElement
    width="100%"
    borderColor={borderColor}
    onClick={(event: SyntheticEvent) => event.stopPropagation()}
    role="menu"
  >
    {dropdown
      .filter((option) => option.isHidden !== true)
      .map((option, i) => {
        const optionContent = option.content ? (
          <DropdownOption
            hoverColor={borderColor}
            isDisabled={option.isDisabled}
            isLoading={option.isLoading}
            isWithButtons={false}
            role="none"
          >
            {option.content}
          </DropdownOption>
        ) : (
          <DropdownOption
            hoverColor={borderColor}
            onClick={option.onClick}
            isDisabled={option.isDisabled}
            isLoading={option.isLoading}
            isWithButtons={option.buttons !== undefined}
            role="menuitem"
          >
            <Flex align="center" gap="5px">
              <Text variant="small1" color={textColor} whiteSpace="nowrap">
                {option.label}
              </Text>
              {option.tooltip && <InfoTooltip tooltip={option.tooltip} />}
            </Flex>

            <Flex gap="5px">
              {option.buttons?.map((optionButton, i) => (
                <Button
                  key={i}
                  size="small"
                  variant="inverted"
                  color={optionButton.color || "greenSmoke"}
                  onClick={optionButton.onClick}
                  onMouseEnter={optionButton.onMouseEnter}
                  onMouseLeave={optionButton.onMouseLeave}
                  isDisabled={optionButton.isDisabled}
                >
                  {optionButton.label}
                </Button>
              ))}
            </Flex>

            {option.isLoading && <Icon name="spinner" size={14} />}
          </DropdownOption>
        );

        return (
          <React.Fragment key={i}>
            {i !== 0 && (
              <Flex margin="0 5px">
                <HorizontalDivider color={Color.lightGrayHover} />
              </Flex>
            )}

            {!option.isDisabled && option.url ? (
              <Link to={option.url}>{optionContent}</Link>
            ) : (
              optionContent
            )}
          </React.Fragment>
        );
      })}
  </DropdownElement>
);
